import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESETS = {
    "NL 2/5 $200-$500":   { sb: "2", bb: "5",  label: "NL 2/5 · $200–$500" },
    "NL 5/5 $300-$1000":  { sb: "5", bb: "5",  label: "NL 5/5 · $300–$1000" },
    "NL 5/10 $1000-$3000":{ sb: "5", bb: "10", label: "NL 5/10 · $1000–$3000" },
};

const DEFAULT_GAME = "NL 5/5 $300-$1000";

export default function NewTableDialog({ onSubmit }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [game, setGame] = useState(DEFAULT_GAME);
    const [sb, setSb] = useState(PRESETS[DEFAULT_GAME].sb);
    const [bb, setBb] = useState(PRESETS[DEFAULT_GAME].bb);
    const [seats, setSeats] = useState("9");
    const [rake, setRake] = useState("5");
    const [cap, setCap] = useState("10");

    const applyPreset = (g) => {
        setGame(g);
        const preset = PRESETS[g];
        if (preset) { setSb(preset.sb); setBb(preset.bb); }
    };

    const create = async () => {
        try {
            await onSubmit({
                name: name || `Table ${game}`,
                game_type: game,
                small_blind: parseFloat(sb),
                big_blind: parseFloat(bb),
                max_seats: parseInt(seats),
                rake_percent: parseFloat(rake),
                rake_cap: parseFloat(cap),
            });
            toast.success("Table opened");
            setOpen(false);
            setName("");
        } catch {
            toast.error("Failed to open table");
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="btn-open-new-table" className="btn-gold rounded-md h-11 px-5">
                    <PlusCircle size={18} weight="bold" className="mr-2" />
                    Open new table
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Open new table</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <Label className="label-mini">Table name</Label>
                        <Input data-testid="input-table-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Cash Table 1" />
                    </div>
                    <div>
                        <Label className="label-mini">Game</Label>
                        <Select value={game} onValueChange={applyPreset}>
                            <SelectTrigger data-testid="select-game-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(PRESETS).map(([v, p]) => (
                                    <SelectItem key={v} value={v}>{p.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-mini">Seats</Label>
                        <Select value={seats} onValueChange={setSeats}>
                            <SelectTrigger data-testid="select-max-seats"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {[6, 7, 8, 9, 10].map(n => <SelectItem key={n} value={String(n)}>{n} seats</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-mini">Small Blind</Label>
                        <Input data-testid="input-sb" value={sb} onChange={(e) => setSb(e.target.value)} />
                    </div>
                    <div>
                        <Label className="label-mini">Big Blind</Label>
                        <Input data-testid="input-bb" value={bb} onChange={(e) => setBb(e.target.value)} />
                    </div>
                    <div>
                        <Label className="label-mini">Rake %</Label>
                        <Input data-testid="input-rake" value={rake} onChange={(e) => setRake(e.target.value)} />
                    </div>
                    <div>
                        <Label className="label-mini">Rake cap ($)</Label>
                        <Input data-testid="input-rake-cap" value={cap} onChange={(e) => setCap(e.target.value)} />
                    </div>
                </div>
                <DialogFooter>
                    <Button data-testid="btn-create-table" onClick={create} className="btn-gold">Open table</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
